import React, { useEffect } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';
import { setAuthProvider } from '../services/authProviderRegistry';
import { ScimAuthService } from '../services/ScimAuthService';
import { loginRequest } from '../config/authConfig';
import { stashHashRouteForMsalRedirect, getStashedMsalRedirectRoute, clearStashedMsalRedirectRoute } from '../utils/msalRedirectUtils';
import { isMsalClientIdConfigured, logMsalConfigDiagnostics } from '../utils/envUtils';
import { debugLog, isMsalDiagEnabled } from '../utils/debugLog';

// MSAL instance should be a singleton. It should be initialized outside of the component tree.
let msalInstance: PublicClientApplication;
let msalInitialized: Promise<void> | null = null;
const msalClientIdConfigured = isMsalClientIdConfigured(msalConfig.auth.clientId);
logMsalConfigDiagnostics('AuthProvider module load', {
    msalClientIdConfigured,
    msalClientIdFromConfig: msalConfig.auth.clientId,
    redirectUri: window.location.origin
});

function createFallbackMsalInstance(): PublicClientApplication {
    logMsalConfigDiagnostics('AuthProvider createFallbackMsalInstance');
    return new PublicClientApplication({
        auth: {
            clientId: '',
            authority: 'https://login.microsoftonline.com/common',
            redirectUri: window.location.origin,
        }
    });
}

try {
    logMsalConfigDiagnostics('AuthProvider creating PublicClientApplication from msalConfig', {
        msalClientId: msalConfig.auth.clientId,
        msalAuthority: msalConfig.auth.authority,
        msalRedirectUri: msalConfig.auth.redirectUri
    });
    msalInstance = new PublicClientApplication(msalConfig);

    // Initialize MSAL before calling any other methods
    msalInitialized = msalInstance.initialize().then(() => {
        if (isMsalDiagEnabled()) {
            debugLog('✅ MSAL: Initialized successfully');
        }

        // Handle redirect promise after initialization
        return msalInstance.handleRedirectPromise().then((response) => {
            if (response && isMsalDiagEnabled()) {
                debugLog('✅ MSAL: Successfully handled redirect response');
            }
            // Clear any hash that might have been left over
            if (window.location.hash && window.location.hash.includes('code=')) {
                const hash = window.location.hash;
                const codeMatch = hash.match(/code=([^&]+)/);
                const stateMatch = hash.match(/state=([^&]+)/);
                const clientInfoMatch = hash.match(/client_info=([^&]+)/);
                const sessionStateMatch = hash.match(/session_state=([^&]+)/);

                if (codeMatch) {
                    const cleanHash = `#code=${codeMatch[1]}${
                        stateMatch ? `&state=${stateMatch[1]}` : ''
                    }${
                        clientInfoMatch ? `&client_info=${clientInfoMatch[1]}` : ''
                    }${
                        sessionStateMatch ? `&session_state=${sessionStateMatch[1]}` : ''
                    }`;
                    window.history.replaceState(null, '', window.location.pathname + cleanHash);
                }
            }
        }).catch((error) => {
            console.error('❌ MSAL: Failed to handle redirect promise:', error);
        });
    }).catch((error) => {
        console.error('❌ MSAL: Failed to initialize:', error);
    });
} catch (error) {
    console.error('Failed to create MSAL instance:', error);
    // Create a fallback instance with minimal config
    msalInstance = createFallbackMsalInstance();
    msalInitialized = msalInstance.initialize().catch((initError) => {
        console.error('Failed to initialize fallback MSAL instance:', initError);
    });
}

export { msalInstance };

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    // Register auth delegate for axios so interceptors use MSAL without axios importing AuthProvider/MSAL
    useEffect(() => {
        logMsalConfigDiagnostics('AuthProvider mount', { msalClientIdConfigured });
        setAuthProvider({
            getAuthToken: () => ScimAuthService.getAuthToken(),
            onAuthRequired: () => {
                logMsalConfigDiagnostics('AuthProvider onAuthRequired invoked', { msalClientIdConfigured });
                if (!isMsalClientIdConfigured(msalConfig.auth.clientId)) {
                    console.warn('⚠️ MSAL redirect skipped because the app is missing a valid client id.');
                    return;
                }
                stashHashRouteForMsalRedirect();
                msalInstance.loginRedirect(loginRequest).catch((loginError) => {
                    console.error('❌ Failed to trigger login redirect:', loginError);
                });
            },
        });
        return () => setAuthProvider(null);
    }, []);

    // Handle redirect promise on mount as well (in case it wasn't handled during initialization)
    useEffect(() => {
        logMsalConfigDiagnostics('AuthProvider redirect handler effect start', { msalClientIdConfigured });
        // Wait for MSAL to be initialized before calling handleRedirectPromise
        const handleRedirect = async () => {
            try {
                // Wait for initialization if it's still in progress
                if (msalInitialized) {
                    await msalInitialized;
                }
                
                // Ensure MSAL is initialized before calling handleRedirectPromise
                if (!msalInstance.getConfiguration()) {
                    await msalInstance.initialize();
                }
                
                const response = await msalInstance.handleRedirectPromise();
                logMsalConfigDiagnostics('AuthProvider handleRedirectPromise resolved', {
                    hasResponse: !!response,
                    responseAccount: response?.account?.username ?? null
                });
                if (response && isMsalDiagEnabled()) {
                    debugLog('✅ MSAL: Successfully handled redirect response on mount');
                    
                    // Clear any hash that might have been left over
                    if (window.location.hash && window.location.hash.includes('code=')) {
                        // Extract just the code and state, remove any app routing
                        const hash = window.location.hash;
                        const codeMatch = hash.match(/code=([^&]+)/);
                        const stateMatch = hash.match(/state=([^&]+)/);
                        const clientInfoMatch = hash.match(/client_info=([^&]+)/);
                        const sessionStateMatch = hash.match(/session_state=([^&]+)/);
                        
                        if (codeMatch) {
                            // Reconstruct clean hash with just auth parameters
                            const cleanHash = `#code=${codeMatch[1]}${
                                stateMatch ? `&state=${stateMatch[1]}` : ''
                            }${
                                clientInfoMatch ? `&client_info=${clientInfoMatch[1]}` : ''
                            }${
                                sessionStateMatch ? `&session_state=${sessionStateMatch[1]}` : ''
                            }`;
                            window.history.replaceState(null, '', window.location.pathname + cleanHash);
                            // Reload to let MSAL process the clean hash
                            window.location.reload();
                            return;
                        }
                    }
                    
                    // After successful authentication, restore the saved route if it exists
                    const savedRoute = getStashedMsalRedirectRoute();
                    if (savedRoute && isMsalDiagEnabled()) {
                        debugLog('🔄 MSAL: Restoring saved route after authentication:', savedRoute);
                        clearStashedMsalRedirectRoute();
                        // Small delay to ensure MSAL has finished processing
                        setTimeout(() => {
                            window.location.hash = savedRoute;
                        }, 100);
                    } else {
                        // Clear any remaining auth parameters from hash
                        if (window.location.hash && (window.location.hash.includes('code=') || window.location.hash.includes('error='))) {
                            window.history.replaceState(null, '', window.location.pathname);
                        }
                    }
                }
            } catch (error) {
                console.error('❌ MSAL: Failed to handle redirect promise on mount:', error);
            }
        };
        
        handleRedirect();
    }, []);

    return (
        <MsalProvider instance={msalInstance}>
            {children}
        </MsalProvider>
    );
}; 